import { useEffect, useRef, useState } from 'react';
import type { DistiListing } from '@techgitter/contracts';
import { searchDistiListings } from '../services/distiListingsApi';

export interface UseDistiListingSearchParams {
  query: string;
  productCode?: string;
  distiId?: string;
  inStockOnly?: boolean;
  // Server-side spec filters. The picker passes these directly so the
  // OData query narrows on the spec_* columns; picking any of them on
  // its own counts as a "search trigger" and fires a fetch.
  computerType?: string;            // 'Laptop' | 'Desktop' | 'AIO'
  screenSize?: string;              // bucket key: 'smaller'|'13'|'14'|'15-16'|'bigger'
  cpuBuckets?: string[];            // ['intel-5', 'amd', ...]
  ramGb?: number[];                 // [8, 16, 32]
  storageGb?: number[];             // [256, 512, 1024]
}

export interface UseDistiListingSearchResult {
  results: DistiListing[];
  isLoading: boolean;
  error: string | null;
}

const DEBOUNCE_MS = 250;

/**
 * Debounced disti-listing search.
 *
 * Mirrors the shape of useQuoteOptionSearch — fires 250ms after either
 * `query` or any of the filter args settle, aborts the in-flight request
 * when params change again, and returns `{ results, isLoading, error }`.
 *
 * Unlike the price-book search, the empty-query case still fires when a
 * `productCode` filter is set, because "all laptops" is a useful list on
 * its own.
 */
export function useDistiListingSearch(
  params: UseDistiListingSearchParams,
): UseDistiListingSearchResult {
  const {
    query,
    productCode,
    distiId,
    inStockOnly,
    computerType,
    screenSize,
    cpuBuckets,
    ramGb,
    storageGb,
  } = params;
  const [results, setResults] = useState<DistiListing[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  // Stable serialised form so array/list params don't trigger a refetch
  // every render purely due to identity churn — the effect's dep array
  // compares strings, not references.
  const cpuBucketsParam = cpuBuckets?.length ? cpuBuckets.join(',') : '';
  const ramGbParam = ramGb?.length ? ramGb.join(',') : '';
  const storageGbParam = storageGb?.length ? storageGb.join(',') : '';

  useEffect(() => {
    const trimmed = query.trim();
    const hasFilter = Boolean(
      trimmed ||
        productCode ||
        distiId ||
        computerType ||
        screenSize ||
        cpuBucketsParam ||
        ramGbParam ||
        storageGbParam,
    );
    if (!hasFilter) {
      setResults([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const handle = window.setTimeout(async () => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        const data = await searchDistiListings({
          q: trimmed || undefined,
          productCode: productCode || undefined,
          distiId: distiId || undefined,
          inStockOnly: inStockOnly ?? false,
          computerType: computerType || undefined,
          screenSize: screenSize || undefined,
          cpuBuckets: cpuBucketsParam || undefined,
          ramGb: ramGbParam || undefined,
          storageGb: storageGbParam || undefined,
          page: 1,
          pageSize: 50,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setResults(data.items);
        }
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
        setResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
      controllerRef.current?.abort();
    };
  }, [
    query,
    productCode,
    distiId,
    inStockOnly,
    computerType,
    screenSize,
    cpuBucketsParam,
    ramGbParam,
    storageGbParam,
  ]);

  return { results, isLoading, error };
}
